import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { useBusyBlockerContext } from "../../../contexts/busy-blocker-context";
import { useDashboardContext } from "../../../contexts/dashboard-context";
import {
	AlternativeProductGroup,
	useAlternativeProducts,
} from "../../../hooks/useAlternativeProducts";
import { useCart } from "../../../hooks/useCart";
import { useDefinition } from "../../../hooks/useDefinition";
import { useEditor } from "../../../hooks/useEditor";
import { useErrorHandling } from "../../../hooks/useErrorHandling";
import { useLabels } from "../../../hooks/useLabels";
import { useProduct } from "../../../hooks/useProduct";
import { useSelectedAlternativeProduct as useSelectedAlternativeProductCode } from "../../../hooks/useSelectedAlternativeProduct";
import { BottomMenuPanel } from "../../bottom-menu-panel";
import { BottomMenuButton } from "../../bottom-menu/bottom-menu-button";
import { BottomMenuButtons, IBottomMenuButtonsRef } from "../../bottom-menu/buttons";
import { showDialogAsync } from "../../dialog-queue";
import { ConfirmDialog } from "../../dialog/confirm";
import { showInfoDialogAsync } from "../../dialogs/info-dialog";
import { useSelectedAlternativeProductGroupState } from "./hooks";
import { labels } from "./labels";

import { useClient } from "../../../hooks/useClient";
import { useResetDefinitionForOptions } from "../../../hooks/useResetDefinitionForOptions";
import { AlternativeIcon } from "../../alternative-icon/alternative-icon";

export const AlternativeProductsBottomMenuPanel = () => {
	const editor = useEditor();
	const client = useClient();
	const { createErrorHandler } = useErrorHandling();
	const dashboard = useDashboardContext();
	const definition = useDefinition();

	const bottomMenuRef = useRef<IBottomMenuButtonsRef>(null);

	const [alternativeProductGroup] = useSelectedAlternativeProductGroupState();

	const { isPreviousData, alternativeProductsGroups } = useAlternativeProducts();

	const [definitionToReuse, setDefinitionToReuse] = useResetDefinitionForOptions();

	const selectedAlternativeProductCode = useSelectedAlternativeProductCode();
	const { busyBlocker } = useBusyBlockerContext();

	const { designNotAvailableDescription, designNotAvailableTitle, proceed, back } = labels;

	const product = useProduct();

	const [[attributeName, productUnavailable]] = useLabels([
		labels.attributeName(product?.attributeTypeCode),
		labels.productAttributeNotAvailable,
	]);

	const newAlternativeProductsGroup: AlternativeProductGroup | undefined = useMemo(() => {
		return alternativeProductsGroups?.find(
			(group) => group.name === alternativeProductGroup?.name,
		);
	}, [alternativeProductGroup?.name, alternativeProductsGroups]);

	useEffect(() => {
		const index = newAlternativeProductsGroup?.alternativeProducts.findIndex(
			(newProduct) => newProduct.alternativeProductCode === selectedAlternativeProductCode,
		);

		if (index !== undefined && index >= 0)
			bottomMenuRef.current?.scrollIntoView(index, { animate: false });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [newAlternativeProductsGroup?.alternativeProducts]);

	useEffect(() => {
		const index = newAlternativeProductsGroup?.alternativeProducts.findIndex(
			(newProduct) => newProduct.alternativeProductCode === selectedAlternativeProductCode,
		);

		if (index !== undefined && index >= 0) bottomMenuRef.current?.scrollIntoView(index);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedAlternativeProductCode]);

	const setNewAlternativeProduct = useCallback(
		(code: eXpress.AlternativeProductCode) => {
			if (!definition) return;

			if (!definitionToReuse) {
				const copyApiData = editor.client.copyApiData?.bind(editor.client);
				setDefinitionToReuse(definition.clone(copyApiData));
			}

			busyBlocker
				.during(dashboard.applyAlternativeProductAsync(code, definitionToReuse))
				.catch(createErrorHandler());
		},
		[
			definition,
			definitionToReuse,
			busyBlocker,
			dashboard,
			createErrorHandler,
			setDefinitionToReuse,
			editor,
		],
	);

	const shoppingCart = useCart();
	const onClick = useCallback(
		async (code: eXpress.AlternativeProductCode) => {
			const selectedAlternativeProduct =
				newAlternativeProductsGroup?.alternativeProducts.find(
					(prod) => prod.alternativeProductCode === code,
				);
			// don't do anything if we are showing outdated data from previous fetch
			if (!isPreviousData) {
				// check if selected alternative product has required attributes
				if (selectedAlternativeProduct) {
					const selectedQuantities = shoppingCart?.quantities.reduce<
						eXpress.ProductAttributeCode[]
					>((p, c) => {
						if (c.attributeCode !== undefined) {
							p.push(c.attributeCode);
						}
						return p;
					}, []);

					if (selectedQuantities?.length) {
						const alternativeOptions = await client
							.getProductAttributesAsync(selectedAlternativeProduct.productCode)
							.catch(createErrorHandler());
						if (!alternativeOptions) return;

						const availableAttributeCodes = alternativeOptions.attributes?.values.map(
							(attribute) => attribute.code,
						);
						const unavailableAlternativeOptions = selectedQuantities.reduce<
							eXpress.ProductAttributeCode[]
						>((p, c) => {
							if (!availableAttributeCodes?.includes(c)) {
								p.push(c);
							}
							return p;
						}, []);

						if (
							unavailableAlternativeOptions &&
							unavailableAlternativeOptions.length > 0
						) {
							const unavailableAttributes = unavailableAlternativeOptions.map(
								(attr) => attr.toUpperCase(),
							);
							const message =
								productUnavailable && attributeName
									? eXpress.utils.formatString(
											productUnavailable,
											selectedAlternativeProduct.name,
											attributeName,
											unavailableAttributes.toString(),
									  )
									: "";

							showInfoDialogAsync({
								messageHtml: message,
							}).catch(createErrorHandler());
							return;
						}
					}
					if (selectedAlternativeProduct && selectedAlternativeProduct.willResetDesign) {
						showDialogAsync(({ resolve }, { isOpen }) => {
							return (
								<ConfirmDialog
									cancelLabel={back}
									titleLabel={designNotAvailableTitle}
									isOpen={isOpen}
									okLabel={proceed}
									messageHtmlLabel={designNotAvailableDescription}
									onOk={() => {
										setNewAlternativeProduct(code);
										resolve(undefined);
									}}
									onCancel={() => resolve(false)}
								/>
							);
						}).catch(createErrorHandler());
					} else {
						setNewAlternativeProduct(code);
					}
				}
			}
		},
		[
			newAlternativeProductsGroup?.alternativeProducts,
			isPreviousData,
			shoppingCart?.quantities,
			client,
			createErrorHandler,
			attributeName,
			back,
			proceed,
			productUnavailable,
			designNotAvailableTitle,
			designNotAvailableDescription,
			setNewAlternativeProduct,
		],
	);

	if (!newAlternativeProductsGroup) return null;

	return (
		<BottomMenuPanel>
			<BottomMenuButtons ref={bottomMenuRef}>
				{newAlternativeProductsGroup.alternativeProducts.map(
					({ name, alternativeProductCode, icon, orientation }) => (
						<BottomMenuButton
							onClick={() => {
								// eslint-disable-next-line @typescript-eslint/no-floating-promises
								onClick(alternativeProductCode);
							}}
							title={name}
							key={alternativeProductCode}
							isActive={selectedAlternativeProductCode === alternativeProductCode}
						>
							{icon?.svgUrl &&
							newAlternativeProductsGroup.style !==
								eXpress.core.ListViewStyle.Dropdown ? (
								<AlternativeIcon
									svgUrl={icon.svgUrl}
									color={icon.color}
									overlayColor={icon.overlayColor}
									orientation={orientation}
								/>
							) : null}
						</BottomMenuButton>
					),
				)}
			</BottomMenuButtons>
		</BottomMenuPanel>
	);
};
